import React, { useState, useEffect, useMemo } from 'react';
import EvaluationModal from '../components/EvaluationModal';
import AddCourseModal from '../components/AddCourseModal';
import { useNavigate } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = process.env.REACT_APP_API_BASE || '/.netlify/functions/api';
const COURSES_URL = `${API_BASE}/courses`;
const DOCTOR_META_URL = `${API_BASE}/doctor-meta`;

const itemsPerPage = 8;

const COHORT_LABELS = {
  '23': 'الدفعة القديمة',
  '24': 'الدفعة الجديدة',
};

const stripArabicMarks = (s = '') =>
  s.replace(/[\u0617-\u061A\u064B-\u0652\u0670]/g, '').replace(/\u0640/g, '');
const arVariants = (s = '') =>
  s
    .replace(/[إأآا]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ؤ/g, 'و')
    .replace(/ئ/g, 'ي')
    .replace(/ة/g, 'ه');
const arNorm = (s = '') =>
  arVariants(stripArabicMarks(String(s).trim().toLowerCase()))
    .replace(/[^0-9a-z\u0600-\u06FF\s-]/gi, '')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ');
const keyNorm = (s = '') => arNorm(s).replace(/\s+/g, '');

const djb2 = (str) => {
  let h = 5381 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h >>> 0;
};

const shortCodeFromName = (name = '') => {
  const k = keyNorm(name || '');
  const u32 = djb2(k);
  const b36 = u32.toString(36).toLowerCase();
  return (b36 + '00000000').slice(0, 8);
};

const gradeFromPercent = (p) => {
  if (p == null || isNaN(p)) return 'غير مصنف';
  if (p >= 95) return 'A+';
  if (p >= 90) return 'A';
  if (p >= 85) return 'B+';
  if (p >= 80) return 'B';
  if (p >= 75) return 'C+';
  if (p >= 70) return 'C';
  if (p >= 65) return 'D+';
  if (p >= 60) return 'D';
  return 'F';
};

const getCourseName = (c) => (c?.name || c?.Name || '').toString().trim();
const getCourseCode = (c) => (c?.code || c?.Code || c?.course_code || '').toString().trim();

const getCourseCollege = (c) =>
  (
    c?.college ||
    c?.College ||
    c?.COLLEGE ||
    c?.college_name ||
    c?.CollegeName ||
    ''
  )
    .toString()
    .trim();

const getCourseDepartment = (c) =>
  (
    c?.department ||
    c?.Department ||
    c?.DEPARTMENT ||
    c?.department_name ||
    c?.DepartmentName ||
    ''
  )
    .toString()
    .trim();

const getCoursePrerequisiteName = (c) =>
  (
    c?.prerequisite_name ||
    c?.prerequisiteName ||
    c?.prerequisite ||
    c?.Prerequisite ||
    c?.pre_name ||
    ''
  )
    .toString()
    .trim();

const getCoursePrerequisiteCode = (c) =>
  (
    c?.prerequisite_code ||
    c?.prerequisiteCode ||
    c?.prerequisite_code_text ||
    c?.pre_code ||
    ''
  )
    .toString()
    .trim();

const getCoursePlanNo = (c) => {
  const v =
    c?.plan_no ??
    c?.planNo ??
    c?.plan ??
    c?.batch ??
    c?.Batch ??
    c?.PLAN_NO ??
    null;

  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
};

const formatCohortLabelFromPlanNoText = (planNoText = '') => {
  const raw = String(planNoText || '').trim();
  if (!raw) return '';
  const parts = raw
    .split('/')
    .map((x) => x.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  return parts.map((p) => COHORT_LABELS[p] || p).join(' / ');
};

const splitCodes = (s = '') => {
  const raw = String(s || '').trim();
  if (!raw) return [];
  return raw
    .split('/')
    .map((x) => x.trim())
    .filter(Boolean);
};

const CourseEvaluationPage = () => {
  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);

  const [selectedNameFilter, setSelectedNameFilter] = useState('');

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [ratingsMap, setRatingsMap] = useState({});
  const [viewsMap, setViewsMap] = useState({});

  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedRatingFilter, setSelectedRatingFilter] = useState('التقييم');
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);

  const [selectedCohort, setSelectedCohort] = useState('all');

  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showAddCourseModal, setShowAddCourseModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  const [doctorMetaLoading, setDoctorMetaLoading] = useState(false);
  const [doctorMetaError, setDoctorMetaError] = useState('');
  const [colleges, setColleges] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [departmentsByCollege, setDepartmentsByCollege] = useState({});

  const fetchJSON = async (url) => {
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    const text = await res.text();
    if (!res.ok) throw new Error(text || `HTTP ${res.status}`);
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Invalid JSON response');
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        const base = await fetchJSON(COURSES_URL);
        if (!mounted) return;

        const list = Array.isArray(base) ? base : [];
        setCourses(list);

        const rMap = {};
        list.forEach((c) => {
          const name = getCourseName(c);
          if (!name) return;

          const rating = Number.isFinite(c.avg_rating)
            ? Math.max(0, Math.min(100, Math.round(c.avg_rating)))
            : 0;

          const tags = [];
          const pools = []
            .concat(Array.isArray(c.test_tags) ? c.test_tags : [])
            .concat(Array.isArray(c.attendance_tags) ? c.attendance_tags : [])
            .concat(Array.isArray(c.explanation_tags) ? c.explanation_tags : [])
            .concat(Array.isArray(c.grade_tags) ? c.grade_tags : []);

          const freq = {};
          for (const t of pools) {
            const label =
              t && (t.label || t.Label || t.tag || t.Tag || t.text)
                ? t.label || t.Label || t.tag || t.Tag || t.text
                : null;
            if (!label) continue;
            const key = String(label).trim();
            if (!key) continue;
            freq[key] = (freq[key] || 0) + 1;
          }
          Object.entries(freq)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .forEach(([k]) => tags.push(k));

          rMap[name] = {
            rating,
            tags,
            classification: gradeFromPercent(rating),
          };
        });
        setRatingsMap(rMap);

        const vMap = {};
        list.forEach((c) => {
          const n = getCourseName(c);
          if (!n) return;
          const sc = shortCodeFromName(n);
          vMap[n] = {
            shortCode: sc,
            shortPath: `/course-reviews/${sc}`,
            path: `/course-reviews/${sc}`,
          };
        });
        setViewsMap(vMap);
      } catch (e) {
        console.error('Failed to load courses directory/ratings', e);
        if (!mounted) return;
        setCourses([]);
        setRatingsMap({});
        setViewsMap({});
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setDoctorMetaError('');
        setDoctorMetaLoading(true);
        const data = await fetchJSON(DOCTOR_META_URL);
        if (!mounted) return;

        const cols = Array.isArray(data.colleges) ? data.colleges : [];
        const deps = Array.isArray(data.departments) ? data.departments : [];
        const byCollege =
          data.departmentsByCollege && typeof data.departmentsByCollege === 'object'
            ? data.departmentsByCollege
            : {};

        setColleges(cols);
        setAllDepartments(deps);
        setDepartmentsByCollege(byCollege);
      } catch (err) {
        console.error('Error loading doctor-meta:', err);
        if (!mounted) return;
        setDoctorMetaError('تعذر تحميل بيانات الكليات والأقسام.');
        setColleges([]);
        setAllDepartments([]);
        setDepartmentsByCollege({});
      } finally {
        if (mounted) setDoctorMetaLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const collegeOptions = useMemo(() => ['الكل', ...colleges], [colleges]);

  const departmentOptions = useMemo(() => {
    if (!selectedCollege || selectedCollege === 'الكل') {
      return ['الكل', ...allDepartments];
    }
    const deps = departmentsByCollege[selectedCollege] || [];
    return ['الكل', ...deps];
  }, [selectedCollege, allDepartments, departmentsByCollege]);

  const mergedList = useMemo(() => {
    const byName = {};

    courses.forEach((c, idx) => {
      const name = getCourseName(c);
      if (!name) return;

      const rateInfo = ratingsMap[name] || {
        rating: 0,
        tags: [],
        classification: 'غير مصنف',
      };
      const viewInfo = viewsMap[name] || {
        path: `/course-reviews/${shortCodeFromName(name)}`,
      };

      if (!byName[name]) {
        byName[name] = {
          id: `${name}-${idx}`,
          name,
          codesSet: new Set(),
          collegesSet: new Set(),
          departmentsSet: new Set(),
          planNosSet: new Set(),
          prereqNamesSet: new Set(),
          prereqCodesSet: new Set(),
          rating: Number.isFinite(rateInfo.rating) ? rateInfo.rating : 0,
          classification: rateInfo.classification || 'غير مصنف',
          tags: Array.isArray(rateInfo.tags) ? rateInfo.tags : [],
          profilePath: viewInfo.path,
        };
      }

      const code = getCourseCode(c);
      const college = getCourseCollege(c);
      const department = getCourseDepartment(c);
      const planNo = getCoursePlanNo(c);
      const preName = getCoursePrerequisiteName(c);
      const preCode = getCoursePrerequisiteCode(c);

      if (code) byName[name].codesSet.add(code);
      if (college) byName[name].collegesSet.add(college);
      if (department) byName[name].departmentsSet.add(department);
      if (planNo != null) byName[name].planNosSet.add(String(planNo));
      if (preName) byName[name].prereqNamesSet.add(preName);
      if (preCode) byName[name].prereqCodesSet.add(preCode);
    });

    return Object.values(byName).map((entry) => {
      const codesArray = Array.from(entry.codesSet);
      const collegesArray = Array.from(entry.collegesSet);
      const departmentsArray = Array.from(entry.departmentsSet);
      const planNosArray = Array.from(entry.planNosSet);
      const prereqNamesArray = Array.from(entry.prereqNamesSet);
      const prereqCodesArray = Array.from(entry.prereqCodesSet);

      return {
        id: entry.id,
        name: entry.name,
        code: codesArray.join(' / '),
        college: collegesArray.join(' / '),
        department: departmentsArray.join(' / '),
        colleges: collegesArray,
        departments: departmentsArray,
        plan_nos: planNosArray,
        plan_no: planNosArray.join(' / ') || '',
        prerequisite_name: prereqNamesArray.join(' / '),
        prerequisite_code: prereqCodesArray.join(' / '),
        rating: entry.rating,
        classification: entry.classification,
        tags: entry.tags,
        profilePath: entry.profilePath,
      };
    });
  }, [courses, ratingsMap, viewsMap]);

  const cohortOptions = useMemo(() => {
    const set = new Set();
    mergedList.forEach((r) => {
      (r.plan_nos || []).forEach((p) => set.add(String(p)));
    });
    const arr = Array.from(set).sort((a, b) => Number(a) - Number(b));
    return ['all', ...arr];
  }, [mergedList]);

  const filtered = useMemo(() => {
    const norm = (s) => (s || '').trim().toLowerCase();
    const selectedName = norm(selectedNameFilter);

    return mergedList.filter((row) => {
      if (selectedCollege && selectedCollege !== 'الكل') {
        const collegesArr = row.colleges || (row.college ? [row.college] : []);
        if (!collegesArr.includes(selectedCollege)) return false;
      }

      if (selectedDepartment && selectedDepartment !== 'الكل') {
        const depsArr = row.departments || (row.department ? [row.department] : []);
        if (!depsArr.includes(selectedDepartment)) return false;
      }

      if (selectedCohort && selectedCohort !== 'all') {
        const plans = row.plan_nos || [];
        if (!plans.includes(String(selectedCohort))) return false;
      }

      if (selectedName && !norm(row.name).includes(selectedName)) return false;

      return true;
    });
  }, [mergedList, selectedCollege, selectedDepartment, selectedNameFilter, selectedCohort]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (selectedRatingFilter === 'الأكثر تقييما') arr.sort((a, b) => b.rating - a.rating);
    else if (selectedRatingFilter === 'الأقل تقييما') arr.sort((a, b) => a.rating - b.rating);
    return arr;
  }, [filtered, selectedRatingFilter]);

  const totalPages = Math.ceil(sorted.length / itemsPerPage) || 1;
  const currentCourses = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sorted.slice(start, start + itemsPerPage);
  }, [sorted, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCollege, selectedDepartment, selectedRatingFilter, selectedNameFilter, selectedCohort]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.filter-dropdown')) setShowRatingDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSearchSuggestions = (query) => {
    const q = (query || '').trim();
    if (!q) {
      setSearchSuggestions([]);
      setShowSearchSuggestions(false);
      return;
    }
    const norm = (s) => (s || '').trim().toLowerCase();
    const sugg = courses.filter((c) => norm(getCourseName(c)).includes(norm(q))).slice(0, 8);
    setSearchSuggestions(sugg);
    setShowSearchSuggestions(sugg.length > 0);
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQuery(v);
    getSearchSuggestions(v);
  };

  const handleSuggestionSelect = (sug) => {
    const pickedName = getCourseName(sug);
    setSelectedNameFilter(pickedName);
    setSearchQuery(pickedName);
    setShowSearchSuggestions(false);
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const typed = (searchQuery || '').trim();
      if (!typed) {
        setSelectedNameFilter('');
        setShowSearchSuggestions(false);
        setCurrentPage(1);
        return;
      }
      if (searchSuggestions.length > 0) {
        handleSuggestionSelect(searchSuggestions[0]);
      } else {
        setSelectedNameFilter(typed);
        setShowSearchSuggestions(false);
        setCurrentPage(1);
      }
    }
  };

  const handleCourseClick = (course) => {
    const href = course?.profilePath || `/course-reviews/${shortCodeFromName(course?.name)}`;
    navigate(href);
  };

  const handleAddCourse = (courseData) => {
    console.log('Adding course:', courseData);
  };

  const getRatingColor = (rating) => {
    if (rating >= 80) return 'rgba(162, 233, 189, 1)';
    if (rating >= 60) return 'rgba(242, 194, 102, 1)';
    if (rating >= 30) return 'rgba(250, 134, 134, 1)';
    return 'rgba(255, 0, 0, 1)';
  };

  const renderProgressCircle = (rating) => {
    const safe = Number.isFinite(Number(rating)) ? Math.max(0, Math.min(100, Number(rating))) : 0;
    const color = getRatingColor(safe);
    const radius = 35;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (safe / 100) * circumference;

    return (
      <div className="relative w-[80px] h-[80px] md:w-[120px] md:h-[120px]">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} stroke="#374151" strokeWidth="6" fill="transparent" />
          <circle
            cx="60"
            cy="60"
            r={radius}
            stroke={color}
            strokeWidth="6"
            fill="transparent"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
          <circle
            cx="60"
            cy="25"
            r="3"
            fill={color}
            className="transition-all duration-1000 ease-out"
            style={{ transform: `rotate(${(safe / 100) * 360}deg)`, transformOrigin: '60px 60px' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-sm md:text-2xl font-bold">{Math.round(safe)}%</span>
        </div>
      </div>
    );
  };

  const handleSelectEvaluationType = (type) => {
    if (type === 'lecturers') navigate('/lecturer-evaluation');
    else if (type === 'courses') setShowEvaluationModal(false);
  };

  const renderCodeTags = (title, codes) => {
    if (!Array.isArray(codes) || codes.length === 0) return null;

    return (
      <div className="mt-3 w-full">
        <p className="text-gray-400 text-xs mb-1">{title}</p>
        <div className="flex flex-wrap gap-2 justify-end">
          {codes.map((code) => (
            <span
              key={`${title}-${code}`}
              className="flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-xs font-light"
              style={{
                background:
                  'linear-gradient(0deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.04)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%), #081423',
                border: '1px solid rgba(255, 255, 255, 0.7)',
                borderRadius: '24px',
                fontFamily: 'Alexandria, sans-serif',
                fontSize: '10px',
                lineHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'right',
              }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  background: '#FFFFFF',
                  boxShadow: '0px 3px 20px rgba(255, 255, 255, 0.7)',
                  borderRadius: '10px',
                }}
              />
              {code}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderList = (list, page, setPage, totalPagesLocal) => (
    <div className="mb-8">
      <div className="space-y-3 md:space-y-4">
        {list.length > 0 ? (
          list.map((course) => {
            const courseCodes = splitCodes(course.code);
            const prereqCodes = splitCodes(course.prerequisite_code);

            return (
              <div
                key={`list-${course.id}`}
                className="bg-transparent border border-white rounded-xl md:rounded-2xl p-3 md:p-4 flex items-center gap-3 md:gap-5 cursor-pointer hover:bg-[#1a2a3a] transition-colors"
                onClick={() => handleCourseClick(course)}
                title="افتح صفحة المقرر"
              >
                <div className="flex-shrink-0">{renderProgressCircle(course.rating)}</div>

                <div className="flex-1 text-right space-y-1.5 md:space-y-2">
                  <div className="space-y-1 md:space-y-1.5">
                    <p className="text-white text-sm md:text-lg font-medium">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">اسم المقرر:</span>
                      {course.name}
                    </p>

                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">الكلية:</span>
                      {course.college ? course.college : '—'}
                    </p>

                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">القسم:</span>
                      {course.department ? course.department : '—'}
                    </p>

                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">الدفعة:</span>
                        {course.plan_no ? formatCohortLabelFromPlanNoText(course.plan_no) : '—'}

                    </p>

                    <p className="text-gray-300 text-xs md:text-base">
                      <span className="text-gray-400 ml-1 md:ml-2 text-xs md:text-base">المتطلب:</span>
                      {course.prerequisite_name ? course.prerequisite_name : 'لايوجد'}
                    </p>
                  </div>

                  {renderCodeTags('رموز المقرر', courseCodes)}
                  {renderCodeTags('رموز المتطلب', prereqCodes)}

                  {Array.isArray(course.tags) && course.tags.length > 0 ? (
                    <div className="mt-3 flex gap-1 md:gap-2 justify-end flex-wrap">
                      {course.tags.slice(0, 5).map((tag, i) => (
                        <span
                          key={`${tag}-${i}`}
                          className="flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-[10px] md:text-xs font-light"
                          style={{
                            background:
                              'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                            border: '1px solid #2A9E8D',
                            borderRadius: '24px',
                            fontFamily: 'Alexandria, sans-serif',
                            lineHeight: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'right',
                          }}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              background: '#5FCC9C',
                              boxShadow: '0px 3px 20px rgba(162, 233, 189, 0.6)',
                              borderRadius: '10px',
                            }}
                          />
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10">
            <div className="text-gray-400 text-lg md:text-xl mb-2">لا توجد نتائج</div>
            <p className="text-gray-500 text-sm md:text-base">جرّب تغيير الفلاتر أو البحث عن مقرر آخر</p>
          </div>
        )}
      </div>

{totalPages > 1 && (
  <div className="relative mt-8 flex items-center justify-center px-4 md:px-0">
    {                   }
    {currentPage > 1 && (
      <button
        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        className="absolute left-0 flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm transition-colors text-gray-300 hover:text-white whitespace-nowrap"
      >
        <span className="text-base md:text-lg">&lt;</span>
        <span className="hidden sm:inline">السابق</span>
      </button>
    )}

    {                     }
    <div className="flex items-center justify-center gap-2 md:gap-3 overflow-x-auto max-w-full">
      {(() => {
        const pages = [];
        const maxVisible = typeof window !== 'undefined' && window.innerWidth < 768 ? 5 : 7;

        if (totalPages <= maxVisible) {
          for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
          let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
          let end = Math.min(totalPages, start + maxVisible - 1);
          if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);

          if (start > 1) {
            pages.push(1);
            if (start > 2) pages.push('...');
          }
          for (let i = start; i <= end; i++) pages.push(i);
          if (end < totalPages) {
            if (end < totalPages - 1) pages.push('...');
            pages.push(totalPages);
          }
        }

        return pages.map((p, idx) => (
          <button
            key={`${p}-${idx}`}
            onClick={() => typeof p === 'number' && setCurrentPage(p)}
            className={`px-2 md:px-3 py-2 text-xs md:text-sm transition-colors whitespace-nowrap ${
              typeof p === 'number' && currentPage === p
                ? 'text-[#28e3da] font-medium'
                : typeof p === 'number'
                ? 'text-gray-300 hover:text-white'
                : 'text-gray-500 cursor-default'
            }`}
            disabled={typeof p !== 'number'}
          >
            {p}
          </button>
        ));
      })()}
    </div>

    {                   }
    {currentPage < totalPages && (
      <button
        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        className="absolute right-0 flex items-center gap-1 md:gap-2 px-2 md:px-3 py-2 text-xs md:text-sm transition-colors text-gray-300 hover:text-white whitespace-nowrap"
      >
        <span className="hidden sm:inline">التالي</span>
        <span className="text-base md:text-lg">&gt;</span>
      </button>
    )}
  </div>
)}

    </div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <>
      <EvaluationModal
        isOpen={showEvaluationModal}
        onClose={() => setShowEvaluationModal(false)}
        onSelectEvaluationType={handleSelectEvaluationType}
      />

      <AddCourseModal
        isOpen={showAddCourseModal}
        onClose={() => setShowAddCourseModal(false)}
        onSubmit={handleAddCourse}
      />

      <div className="min-h-screen text-white bg-transparent relative z-10 pt-12 md:pt-12 px-4 md:px-8 md:mr-[80px]" dir="ltr">
        <div className="text-center mb-8 md:mb-10">
          <h1 className="text-2xl md:text-4xl font-bold mb-2 md:mb-4">تقييم المقررات</h1>
          <p className="text-gray-300 text-sm md:text-lg px-4">اطلع على تقييمات المقررات وتجارب الطلاب معها</p>
        </div>

        {doctorMetaError && (
          <div className="mb-4 text-center text-xs md:text-sm text-amber-300">{doctorMetaError}</div>
        )}

        <div className="mb-6 md:mb-8">
          <div className="relative mb-4 md:mb-6">
            <div className="relative">
              <input
                type="text"
                placeholder="ابحث عن المقرر..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => getSearchSuggestions(searchQuery)}
                onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 100)}
                dir="rtl"
                className="w-full bg-[#0f1e2e] border border-[#28e3da40] rounded-xl px-12 py-3 md:py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#28e3da] transition-colors text-sm md:text-base text-right"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 z-[999]">
                <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {showSearchSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-[#1a2a3a] border border-[#28e3da40] rounded-lg shadow-lg z-[999] max-h-60 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {searchSuggestions.map((sug, idx) => {
                  const sugName = getCourseName(sug);
                  const sugCode = getCourseCode(sug);
                  const sugCollege = getCourseCollege(sug);
                  const sugDepartment = getCourseDepartment(sug);

                  return (
                    <button
                      key={`${sugName || sug.id || idx}-${idx}`}
                      onMouseDown={() => handleSuggestionSelect(sug)}
                      className="w-full text-right px-4 py-3 text-sm hover:bg-[#2a3a4a] transition-colors text-white border-b border-[#28e3da20] last:border-b-0"
                    >
                      <div className="flex flex-col items-end text-right w-full">
                        <span className="font-medium text-white w-full text-right">{sugName}</span>
                        <span className="text-gray-400 text-xs w-full text-right">
                          {sugCode ? `${sugCode} — ` : ''}
                          {sugCollege || '—'}
                          {sugDepartment ? ` — ${sugDepartment}` : ''}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedNameFilter && (
            <div className="mb-4 flex justify-end">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs"
                style={{
                  background:
                    'linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423',
                  border: '1px solid rgba(40, 227, 218, 0.45)',
                }}
              >
                <span className="text-white font-medium">{selectedNameFilter}</span>
                <span className="text-white/80">:المقرر</span>
                <button
                  onClick={() => {
                    setSelectedNameFilter('');
                    setSearchQuery('');
                  }}
                  className="ml-1 text-white/70 hover:text-white"
                  aria-label="إزالة فلتر الاسم"
                  title="إزالة فلتر الاسم"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end" dir="ltr">
            <div className="relative filter-dropdown">
              <select
                value={selectedCollege || 'الكل'}
                onChange={(e) => setSelectedCollege(e.target.value)}
                className="bg-transparent border border-gray-400 text-white rounded-xl px-4 py-2 hover:bg-[#1a2a3a] focus:outline-none"
                dir="rtl"
                disabled={doctorMetaLoading && !colleges.length}
              >
                {collegeOptions.map((c) => (
                  <option className="bg-[#0f1e2e]" key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative filter-dropdown">
              <select
                value={selectedDepartment || 'الكل'}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="bg-transparent border border-gray-400 text-white rounded-xl px-4 py-2 hover:bg-[#1a2a3a] focus:outline-none"
                dir="rtl"
                disabled={doctorMetaLoading && !allDepartments.length}
              >
                {departmentOptions.map((d) => (
                  <option className="bg-[#0f1e2e]" key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative filter-dropdown">
              <select
                value={selectedCohort || 'all'}
                onChange={(e) => setSelectedCohort(e.target.value)}
                className="bg-transparent border border-gray-400 text-white rounded-xl px-4 py-2 hover:bg-[#1a2a3a] focus:outline-none"
                dir="rtl"
              >
                {cohortOptions.map((c) => (
                  <option className="bg-[#0f1e2e]" key={c} value={c}>
                    {c === 'all' ? 'الدفعة: الكل' : `الدفعة: ${COHORT_LABELS[String(c)] || String(c)}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="relative filter-dropdown">
              <button
                onClick={() => setShowRatingDropdown(!showRatingDropdown)}
                className={`transition-all duration-200 flex items-center gap-3 ${
                  selectedRatingFilter !== 'التقييم'
                    ? 'border border-[#EEEEEE] text-[#EEEEEE]'
                    : 'border border-gray-400 text-white hover:bg-[#1a2a3a]'
                }`}
                style={{
                  width: 'auto',
                  minWidth: selectedRatingFilter !== 'التقييم' ? '128px' : 'auto',
                  height: '44px',
                  borderRadius: '12px',
                  padding: selectedRatingFilter !== 'التقييم' ? '13px 32px' : '12px 16px',
                  background:
                    selectedRatingFilter !== 'التقييم'
                      ? 'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'
                      : 'transparent',
                  fontFamily: 'Alexandria, sans-serif',
                  fontWeight: '800',
                  fontSize: '14px',
                  lineHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <span>{selectedRatingFilter}</span>
                {selectedRatingFilter === 'التقييم' && (
                  <div className="w-4 h-4 border border-white rounded-full flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[3px] border-r-[3px] border-t-[3px] border-transparent border-t-white" />
                  </div>
                )}
              </button>

              {showRatingDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-[#0f1e2e] border border-[#28e3da40] rounded-lg shadow-lg z-10">
                  {['التقييم', 'الأكثر تقييما', 'الأقل تقييما'].map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        setSelectedRatingFilter(option);
                        setShowRatingDropdown(false);
                      }}
                      className={`w-full text-right px-3 py-2 text-xs md:text-sm hover:bg-[#28e3da20] transition-colors text-right ${
                        selectedRatingFilter === option ? 'text-[#28e3da]' : 'text-white'
                      }`}
                      style={{ textAlign: 'right' }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mb-4">
          <p className="text-gray-300 text-sm md:text-base">
            عرض {currentCourses.length} من {sorted.length} مقرر
            {(selectedCollege && selectedCollege !== 'الكل') ||
            (selectedDepartment && selectedDepartment !== 'الكل') ||
            (selectedCohort && selectedCohort !== 'all') ||
            selectedNameFilter ? (
              <span className="text-[#28e3da]"> (مفلتر)</span>
            ) : null}
            {selectedRatingFilter !== 'التقييم' && <span className="text-[#28e3da]"> (مرتب)</span>}
          </p>
        </div>

        {renderList(currentCourses, currentPage, setCurrentPage, totalPages)}

        <div className="mt-8 mb-8">
          <div
            style={{
              width: '100%',
              height: 'auto',
              padding: '16px 16px',
              background:
                'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
              boxShadow: 'rgba(0, 0, 0, 0.25) 0px 4px 4px',
              borderRadius: 36,
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              display: 'flex',
              cursor: 'pointer',
              transition: '0.3s',
              transform: 'scale(1)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onClick={() => {
              setShowAddCourseModal(true);
            }}
          >
            <div style={{ justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
              <div
                style={{
                  color: 'var(--ألوان-القائمة-text-color, white)',
                  fontSize: 12,
                  fontFamily: 'Alexandria',
                  fontWeight: '500',
                  wordWrap: 'break-word',
                }}
              >
                أضف مقرر
              </div>
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7.50684 12.0008C7.50684 11.8019 7.58585 11.6111 7.72651 11.4705C7.86716 11.3298 8.05792 11.2508 8.25684 11.2508H11.7498V7.75781C11.7498 7.5589 11.8289 7.36813 11.9695 7.22748C12.1102 7.08683 12.3009 7.00781 12.4998 7.00781C12.6987 7.00781 12.8895 7.08683 13.0302 7.22748C13.1708 7.36813 13.2498 7.5589 13.2498 7.75781V11.2508H16.7428C16.9417 11.2508 17.1325 11.3298 17.2732 11.4705C17.4138 11.6111 17.4928 11.8019 17.4928 12.0008C17.4928 12.1997 17.4138 12.3905 17.2732 12.5311C17.1325 12.6718 16.9417 12.7508 16.7428 12.7508H13.2498V16.2438C13.2498 16.4427 13.0302 16.6335 13.0302 16.7741C12.8895 16.9148 12.6987 16.9938 12.4998 16.9938C12.3009 16.9938 12.1102 16.9148 11.9695 16.7741C11.8289 16.6335 11.7498 16.4427 11.7498 16.2438V12.7508H8.25684C8.05792 12.7508 7.86716 12.6718 7.72651 12.5311C7.58585 12.3905 7.50684 12.1997 7.50684 12.0008Z"
                  fill="white"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.81667 3.76856C10.9291 3.4235 14.0702 3.4235 17.1827 3.76856C19.0097 3.97256 20.4847 5.41156 20.6987 7.24856C21.0687 10.4056 21.0687 13.5946 20.6987 16.7516C20.4837 18.5886 19.0087 20.0266 17.1827 20.2316C14.0702 20.5766 10.9291 20.5766 7.81667 20.2316C5.98967 20.0266 4.51467 18.5886 4.30067 16.7516C3.93233 13.5946 3.93233 10.4055 4.30067 7.24856C4.51467 5.41156 5.99067 3.97256 7.81667 3.76856ZM17.0167 5.25856C14.0145 4.92579 10.9848 4.92579 7.98267 5.25856C7.42691 5.32021 6.90817 5.56743 6.51025 5.96028C6.11233 6.35312 5.85846 6.86864 5.78967 7.42356C5.434 10.4645 5.434 13.5366 5.78967 16.5776C5.85867 17.1323 6.11263 17.6476 6.51053 18.0402C6.90844 18.4329 7.42706 18.6799 7.98267 18.7416C10.9597 19.0736 14.0397 19.0736 17.0167 18.7416C17.5721 18.6797 18.0905 18.4326 18.4882 18.0399C18.8859 17.6473 19.1397 17.1321 19.2087 16.5776C19.5643 13.5366 19.5643 10.4645 19.2087 7.42356C19.1395 6.86915 18.8856 6.35421 18.4879 5.96178C18.0902 5.56936 17.5719 5.32234 17.0167 5.26056"
                  fill="white"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CourseEvaluationPage;
